import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Download, RefreshCw, TrendingDown, TrendingUp, Sparkles, PhoneCall, Eye, Lock } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton } from "./TenantShell";
import { tapi, apiErr } from "../../lib/tenant";

const TIER = { heavy: "Heavy weighting", medium: "Medium weighting", negative: "Penalty" };
const fmtKes = (n) => `KES ${Number(n).toLocaleString("en-KE")}`;

function Factor({ f, showFix = true }) {
  const neg = f.tier === "negative";
  const pts = neg ? `${f.points}` : `${f.points}/${f.max}`;
  return (
    <li className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3" data-testid={`lpo-factor-${f.key}`}>
      <span className={"mt-0.5 shrink-0 " + (f.passed ? "text-emerald-600" : neg || f.tier === "heavy" ? "text-red-500" : "text-amber-500")}>{f.passed ? <CheckCircle2 size={16} /> : <XCircle size={16} />}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-bold text-neutral-800">{f.label}</p>
          <span className="text-[11px] font-semibold text-neutral-500">{TIER[f.tier]} · <span className={f.passed ? "text-emerald-700" : "text-red-600"}>{pts} pts</span></span>
        </div>
        <p className="text-xs text-neutral-500">{f.actual}</p>
        {!f.passed && f.fix && showFix && <p className="mt-1 text-xs text-neutral-700"><b>What to do:</b> {f.fix}</p>}
      </div>
    </li>
  );
}

// Three plain-language reasons, shown free before the paid report.
function Reasons({ gaps }) {
  return (
    <ol className="space-y-2" data-testid="lpo-outcome-reasons">
      {gaps.slice(0, 3).map((g, i) => (
        <li key={g.key} className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50/60 px-4 py-3" data-testid={`lpo-reason-${g.key}`}>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500 text-xs font-extrabold text-white">{i + 1}</span>
          <div><p className="text-sm font-bold text-neutral-800">{g.label}</p><p className="text-xs text-neutral-600">{g.actual}</p></div>
        </li>
      ))}
    </ol>
  );
}

function ReportPreview({ sub }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    tapi.get(`/t/${sub}/lpo-report/preview.png`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [sub]);
  if (!src) return null;
  return (
    <div className="relative shrink-0 overflow-hidden rounded-xl border border-white/30 bg-white shadow-lg sm:w-[260px]" data-testid="lpo-report-preview">
      <img src={src} alt="Preview of your LPO Capital Readiness Report (blurred until purchased)" className="block w-full" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/25 text-center text-white">
        <Lock size={22} />
        <p className="text-xs font-extrabold uppercase tracking-wide">Page 2 of your report</p>
        <p className="px-4 text-[11px] text-white/90">Unlocks the moment you pay</p>
      </div>
    </div>
  );
}

function ReportOffer({ price, onBuy, busy, qualified, score, passMark, gapCount, sub }) {
  const short = Math.max(0, (passMark || 70) - (score || 0));
  const bullets = qualified
    ? ["Every factor Lasiri Capital weighed — and the points you earned on each",
       "Why funders insist on each one, so you can speak their language with your Relationship Manager",
       "The exact document pack to bring to your funding conversation"]
    : [`Every one of the ${gapCount || "remaining"} factors costing you points — ranked by how many points each will give you back`,
       "Why funders insist on each one — so you fix what actually moves the decision, not what you guess matters",
       "The exact documents and evidence that close each gap, so your re-application gets a different answer"];
  return (
    <div className="rounded-2xl p-6 text-white shadow-sm sm:p-8" style={{ background: "var(--tenant)" }} data-testid="lpo-report-offer">
     <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
      <div className="min-w-0 flex-1">
      <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/80"><Sparkles size={14} /> {qualified ? "Your full score breakdown" : "Your route to qualifying"}</p>
      <h3 className="mt-2 text-xl font-extrabold leading-snug sm:text-2xl" data-testid="lpo-report-offer-headline">
        {qualified
          ? "Want the full picture of how your score was built — and what funders look for?"
          : "If you want to qualify for LPO funding, download this report — it tells you exactly what you need to do."}
      </h3>
      <p className="mt-2 text-sm text-white/85">
        {qualified
          ? "Your LPO Capital Readiness Report explains every factor Lasiri Capital weighs and why funders care about each one."
          : <>You are <b>{short} point{short === 1 ? "" : "s"}</b> short of the pass mark. The three reasons above are where you are today — the report is the plan for how you get to funded. Applicants who fix what the report tells them to fix come back with a different answer.</>}
      </p>
      <ul className="mt-4 space-y-2" data-testid="lpo-report-offer-bullets">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-white/95"><CheckCircle2 size={16} className="mt-0.5 shrink-0 text-white" /> <span>{b}</span></li>
        ))}
      </ul>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button type="button" onClick={onBuy} disabled={busy} data-testid="lpo-report-buy"
          className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-extrabold shadow-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60" style={{ color: "var(--tenant)" }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} {qualified ? "Get my Capital Readiness Report" : "Show me what I need to do to qualify"} — {fmtKes(price)}
        </button>
        <span className="text-xs text-white/75">One-time payment via Paystack (card or M-Pesa) · opens instantly · a copy is emailed to you</span>
      </div>
      {!qualified && <p className="mt-3 text-xs text-white/70">{fmtKes(price)} once, against an LPO worth hundreds of thousands — the cheapest step in getting it funded.</p>}
      </div>
      <ReportPreview sub={sub} />
     </div>
    </div>
  );
}

function NextStepCard({ brand, requestedAt, onRequest, busy }) {
  if (requestedAt) {
    return (
      <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="lpo-contact-confirmed">
        <CheckCircle2 size={22} className="mt-0.5 shrink-0 text-emerald-600" />
        <div>
          <p className="text-sm font-extrabold text-emerald-900">You're on the list — a {brand} Relationship Manager will contact you within two business days.</p>
          <p className="mt-1 text-xs text-emerald-800">They'll confirm your LPO details, walk you through the facility terms and the next steps to funding. Keep your phone handy.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-emerald-600 p-6 text-white shadow-sm sm:p-7" data-testid="lpo-next-step">
      <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/80"><Sparkles size={14} /> Your next step</p>
      <h3 className="mt-2 text-xl font-extrabold leading-snug sm:text-2xl">Ready to take this to the next stage?</h3>
      <p className="mt-2 text-sm text-white/85">
        A {brand} Relationship Manager can call you to confirm your LPO details, talk you through the facility and get your funding moving. Click below and we'll have them reach out within two business days.
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button type="button" onClick={onRequest} disabled={busy} data-testid="lpo-contact-request"
          className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-extrabold text-emerald-700 shadow-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <PhoneCall size={16} />} Yes — have a Relationship Manager contact me
        </button>
        <span className="text-xs text-white/75">No obligation · we'll use the phone number and email on your account</span>
      </div>
    </div>
  );
}

function RerunButton({ reruns, onRerun, onBuy, busy }) {
  const hasCredit = reruns.total_left > 0;
  return (
    <button type="button" onClick={hasCredit ? onRerun : onBuy} disabled={busy} data-testid="lpo-rerun-btn"
      className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-bold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60">
      {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
      {hasCredit ? `Re-apply with new documents (${reruns.total_left} paid re-run${reruns.total_left === 1 ? "" : "s"} available)` : `Re-apply with new documents — ${fmtKes(reruns.pack_price_kes)}`}
    </button>
  );
}

export const StepLpoOutcome = ({ me, onRerun }) => {
  const { sub, config } = useTenant();
  const brand = (config.eyebrow || "").split("·")[0].trim() || config.name;
  const [outcome, setOutcome] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const payRef = params.get("lpo_report_ref");
  const reruns = me.reruns || { total_left: 0 };

  const load = () => tapi.get(`/t/${sub}/outcome`).then((r) => setOutcome(r.data)).catch((e) => toast.error(apiErr(e, "Couldn't load your result")));
  useEffect(() => { load(); }, [sub]); // eslint-disable-line react-hooks/exhaustive-deps

  const [reportUrl, setReportUrl] = useState(null);
  const reportRef = useRef(null);

  const fetchReport = async () => {
    const { data, headers } = await tapi.get(`/t/${sub}/lpo-report`, { responseType: "blob" });
    const name = (headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "lpo-capital-readiness-report.pdf";
    return { url: URL.createObjectURL(new Blob([data], { type: "application/pdf" })), name };
  };

  const openReport = async () => {
    setBusy(true);
    try {
      const { url } = await fetchReport();
      setReportUrl(url);
      setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) { toast.error(apiErr(e, "Couldn't open your report")); }
    setBusy(false);
  };

  useEffect(() => {
    if (!payRef) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await tapi.post(`/t/${sub}/lpo-report/poll/${payRef}`);
        if (data.unlocked) { toast.success("Payment received — here is your report. A copy is on its way to your email."); setParams({}); load(); openReport(); return; }
        if (data.payment_status === "failed" || tries >= 6) { toast.error("Payment not completed — the report is still locked."); setParams({}); return; }
        tries += 1; setTimeout(poll, 2500);
      } catch (e) { toast.error(apiErr(e, "Couldn't confirm payment")); setParams({}); }
    };
    poll();
  }, [payRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await tapi.post(`/t/${sub}/lpo-report/checkout`);
      if (data.already_unlocked) { load(); return; }
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e, "Couldn't start checkout")); setBusy(false); }
  };

  const buyRerun = async () => {
    setBusy(true);
    try { const { data } = await tapi.post(`/t/${sub}/reruns/checkout`); window.location.assign(data.checkout_url); }
    catch (e) { toast.error(apiErr(e, "Couldn't start checkout")); setBusy(false); }
  };

  const requestContact = async () => {
    setBusy(true);
    try {
      const { data } = await tapi.post(`/t/${sub}/lpo-contact-request`);
      setOutcome((o) => ({ ...o, contact_requested_at: data.requested_at }));
      toast.success(`Request sent — a ${brand} Relationship Manager will be in touch.`);
    } catch (e) { toast.error(apiErr(e, "Couldn't send your request")); }
    setBusy(false);
  };

  const download = async () => {
    setBusy(true);
    try {
      const { url, name } = await fetchReport();
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e, "Couldn't generate your report")); }
    setBusy(false);
  };

  if (!outcome) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;
  const q = outcome.qualified;
  const unlocked = outcome.report_unlocked;
  const gaps = outcome.gaps || [];
  return (
    <div className="space-y-5" data-testid={q ? "lpo-outcome-pass" : "lpo-outcome-fail"}>
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tenant)" }}>LPO Capital Readiness</p>
            <h2 className="mt-2 text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl" data-testid="lpo-outcome-headline">
              {q ? "You prequalify for Lasiri Capital funding." : "You're not quite there yet."}
            </h2>
            <p className="mt-2 text-base text-neutral-600" data-testid="lpo-outcome-message">
              {q ? `Congratulations — your LPO meets ${brand}'s criteria. The next step is a conversation with one of their Relationship Managers.` : "Here are the three main reasons your application did not qualify for Lasiri Capital funding today."}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {unlocked && <BrandButton onClick={download} disabled={busy} data-testid="lpo-report-download">{busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download my Capital Readiness Report</BrandButton>}
              {unlocked && !reportUrl && <button type="button" onClick={openReport} disabled={busy} data-testid="lpo-report-view"
                className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-bold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"><Eye size={14} /> View report here</button>}
              {!q && <RerunButton reruns={reruns} onRerun={onRerun} onBuy={buyRerun} busy={busy} />}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-center rounded-2xl px-6 py-4" style={{ background: q ? "var(--tenant)" : "#F3F4F6" }}>
            <span className={"text-5xl font-extrabold tabular-nums " + (q ? "text-white" : "text-neutral-800")} data-testid="lpo-outcome-score">{outcome.score}</span>
            <span className={"text-[11px] font-bold uppercase tracking-wide " + (q ? "text-white/80" : "text-neutral-500")}>of 100 · pass mark {outcome.pass_mark}</span>
          </div>
        </div>
      </div>

      {q && <NextStepCard brand={brand} requestedAt={outcome.contact_requested_at} onRequest={requestContact} busy={busy} />}
      {reportUrl && (
        <section ref={reportRef} className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm" data-testid="lpo-report-viewer">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-5 py-3">
            <div>
              <p className="text-sm font-extrabold text-neutral-800">Your LPO Capital Readiness Report</p>
              <p className="text-xs text-neutral-500">A copy has also been sent to {me.user?.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <a href={reportUrl} target="_blank" rel="noreferrer" data-testid="lpo-report-open-tab"
                className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50"><Eye size={13} /> Open in new tab</a>
              <BrandButton onClick={download} disabled={busy} data-testid="lpo-report-download-inline"><Download size={14} /> Download PDF</BrandButton>
            </div>
          </div>
          <object data={reportUrl} type="application/pdf" className="h-[80vh] w-full" aria-label="LPO Capital Readiness Report">
            <div className="flex h-[40vh] flex-col items-center justify-center gap-3 p-8 text-center text-sm text-neutral-600">
              <p>Your browser can't preview PDFs here — use the buttons above to open or download your report.</p>
            </div>
          </object>
        </section>
      )}
      {q && !unlocked && <ReportOffer sub={sub} price={outcome.report_price_kes} onBuy={buy} busy={busy} qualified score={outcome.score} passMark={outcome.pass_mark} gapCount={gaps.length} />}

      {!q && !unlocked && (
        <>
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-extrabold text-neutral-800"><TrendingDown size={16} className="text-red-500" /> Why you didn't qualify</h3>
            <Reasons gaps={gaps} />
            {gaps.length > 3 && <p className="mt-2 text-xs text-neutral-500" data-testid="lpo-more-gaps">+ {gaps.length - 3} more factor{gaps.length - 3 === 1 ? "" : "s"} are also costing you points — the report shows every one and how to fix it.</p>}
          </section>
          <ReportOffer sub={sub} price={outcome.report_price_kes} onBuy={buy} busy={busy} score={outcome.score} passMark={outcome.pass_mark} gapCount={gaps.length} />
        </>
      )}

      {!q && unlocked && gaps.length > 0 && (
        <section>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-extrabold text-neutral-800"><TrendingDown size={16} className="text-red-500" /> What is holding your score down — and how to fix it</h3>
          <ul className="space-y-2" data-testid="lpo-outcome-gaps">{gaps.slice(0, 3).map((f) => <Factor key={f.key} f={f} />)}</ul>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-5 py-4" data-testid="lpo-gaps-more-cta">
            <p className="text-sm text-neutral-700">
              {gaps.length > 3 ? <><b>{gaps.length - 3} more factor{gaps.length - 3 === 1 ? "" : "s"}</b> affecting your score, plus why funders insist on each one and the evidence that closes the gap, are in your report.</>
                : <>Why funders insist on each of these, and the evidence that closes each gap, are in your report.</>}
            </p>
            <BrandButton onClick={download} disabled={busy} data-testid="lpo-gaps-download">{busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download my report to learn more</BrandButton>
          </div>
        </section>
      )}
      <section>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-extrabold text-neutral-800"><TrendingUp size={16} className="text-emerald-600" /> {q ? "How your score was built" : "What is working in your favour"}</h3>
        <ul className="space-y-2" data-testid="lpo-outcome-strengths">{(q ? outcome.factors : outcome.strengths).map((f) => <Factor key={f.key} f={f} />)}</ul>
      </section>
    </div>
  );
};
